import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';

/**
 * HLS Video Player Component
 * Supports adaptive bitrate streaming with quality selection
 * Falls back to native HLS on Safari
 */
const HLSVideoPlayer = forwardRef(({
  src,
  poster,
  autoPlay = false,
  muted = true,
  loop = true,
  playsInline = true,
  className = '',
  style = {},
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  onError,
  onQualityChange,
  onBuffering,
}, ref) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1); // -1 = auto

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
    get currentTime() { return videoRef.current?.currentTime || 0; },
    set currentTime(time) { if (videoRef.current) videoRef.current.currentTime = time; },
    get duration() { return videoRef.current?.duration || 0; },
    get paused() { return videoRef.current?.paused ?? true; },
    get muted() { return videoRef.current?.muted ?? true; },
    set muted(val) { if (videoRef.current) videoRef.current.muted = val; },
    setQuality: (levelIndex) => {
      if (hlsRef.current) {
        hlsRef.current.currentLevel = levelIndex;
        setCurrentQuality(levelIndex);
      }
    },
    getQualityLevels: () => qualityLevels,
  }));

  // Initialize HLS or native playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    const isHLSSource = src.endsWith('.m3u8');

    if (isHLSSource && Hls.isSupported()) {
      // Use hls.js for HLS streams
      const hls = new Hls({
        autoStartLoad: true,
        startLevel: -1, // Auto quality
        capLevelToPlayerSize: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        lowLatencyMode: false,
      });

      hls.loadSource(src);
      hls.attachMedia(video);

      // Quality levels available
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        const levels = hls.levels.map((level, index) => ({
          index,
          height: level.height,
          width: level.width,
          bitrate: level.bitrate,
          label: `${level.height}p`,
        }));
        setQualityLevels(levels);
        onQualityChange?.(levels);
      });

      // Quality switched
      hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
        setCurrentQuality(data.level);
      });

      // Buffering state
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        setIsBuffering(false);
        onBuffering?.(false);
      });

      hls.on(Hls.Events.BUFFER_STALLED, () => {
        setIsBuffering(true);
        onBuffering?.(true);
      });

      // Error handling
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('HLS network error, trying to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('HLS media error, trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.error('HLS fatal error:', data);
              hls.destroy();
              onError?.(data);
              break;
          }
        }
      });

      hlsRef.current = hls;

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (isHLSSource && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = src;
    } else {
      // Direct video source (MP4, WebM)
      video.src = src;
    }
  }, [src, onQualityChange, onBuffering, onError]);

  // Auto-play handling
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (autoPlay) {
      video.play().catch((err) => {
        console.log('Autoplay blocked:', err.message);
        // Try muted autoplay
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }, [autoPlay, src]);

  return (
    <div className={`hls-video-container relative ${className}`} style={style}>
      <video
        ref={videoRef}
        poster={poster}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        className="w-full h-full object-contain"
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        onTimeUpdate={onTimeUpdate}
        onWaiting={() => {
          setIsBuffering(true);
          onBuffering?.(true);
        }}
        onPlaying={() => {
          setIsBuffering(false);
          onBuffering?.(false);
        }}
        onError={(e) => onError?.(e.target.error)}
      />

      {/* Buffering indicator */}
      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
        </div>
      )}

      {/* Quality selector (optional - can be controlled externally) */}
      {qualityLevels.length > 1 && (
        <div className="absolute bottom-4 right-4 opacity-0 hover:opacity-100 transition-opacity">
          <select
            value={currentQuality}
            onChange={(e) => {
              const level = parseInt(e.target.value, 10);
              if (hlsRef.current) {
                hlsRef.current.currentLevel = level;
                setCurrentQuality(level);
              }
            }}
            className="bg-black/70 text-white text-sm px-2 py-1 rounded"
          >
            <option value={-1}>Auto</option>
            {qualityLevels.map((level) => (
              <option key={level.index} value={level.index}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
});

HLSVideoPlayer.displayName = 'HLSVideoPlayer';

export default HLSVideoPlayer;
